/**
 * TYPO3 version checker (Vite-bundled via app.ts).
 * AJAX inline results for VersionChecker teaser forms (.btn--check-version).
 * Sales Intelligence forms use a plain .btn and are not handled here.
 */

const STATE_CLASSES = [
  'form-success',
  'form-error',
  'form-check-version',
  'custom-error-msg',
  'form-error-major-version',
  'form-api-error',
];
const LATEST_MAJOR = 14;

function getCustomDomainErrorMessage(formRoot) {
  const fromData = (formRoot.getAttribute('data-invalid-domain-msg') || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (fromData !== '') {
    return fromData;
  }
  const fromDom = formRoot.querySelector('.form-custom-error-msg p');
  if (fromDom) {
    const txt = fromDom.textContent.replace(/\s+/g, ' ').trim();
    if (txt !== '') {
      return txt;
    }
  }
  return '';
}

function resetFormState(formRoot) {
  if (!formRoot) {
    return;
  }
  formRoot.classList.remove(...STATE_CLASSES);
  const apiEl = formRoot.querySelector('.form-api-error-msg');
  if (apiEl) {
    apiEl.textContent = '';
  }
  const majorEl = formRoot.querySelector('.form-major-version-msg');
  if (majorEl) {
    majorEl.textContent = '';
  }
}

function showErrorMessage(formRoot, message) {
  resetFormState(formRoot);
  const text = (message || '').trim();
  if (!text) {
    return;
  }
  const apiEl = formRoot.querySelector('.form-api-error-msg');
  if (apiEl) {
    apiEl.textContent = text;
  }
  formRoot.classList.add('form-api-error');
}

function showCustomDomainError(formRoot) {
  resetFormState(formRoot);
  const message = getCustomDomainErrorMessage(formRoot);
  const customEl = formRoot.querySelector('.form-custom-error-msg p');
  if (customEl && message) {
    customEl.textContent = message;
  }
  formRoot.classList.add('custom-error-msg');
}

function isTypo3Site(data) {
  if (!data || typeof data !== 'object') {
    return false;
  }
  return data.is_typo3 === true || data.po3 === true || data.typo3 === true;
}

function backendErrorText(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const out = data.output;
  const msg = data.message;
  if (typeof out === 'string' && out.trim() !== '') {
    return out.trim();
  }
  if (typeof msg === 'string' && msg.trim() !== '') {
    return msg.trim();
  }
  return '';
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

function shouldUseCustomDomainError(data) {
  if (!data || typeof data !== 'object') {
    return true;
  }

  if (data.domain_check_failed === true || isTruthyFlag(data.failed)) {
    return true;
  }

  const msg = backendErrorText(data);
  if (msg) {
    const lower = msg.toLowerCase();
    if (
      lower.includes('5xx')
      || lower.includes('webserver')
      || lower.includes('misconfigured')
      || lower.includes('could not')
      || lower.includes('unable to')
      || lower.includes('unreachable')
      || lower.includes('not resolve')
      || lower.includes('does not exist')
      || lower.includes('not reachable')
      || lower.includes('url does not')
      || lower.includes('invalid domain')
    ) {
      return true;
    }
  }

  if (!isTypo3Site(data)) {
    if (data.success !== false && !isTruthyFlag(data.failed) && data.domain_check_failed !== true) {
      return false;
    }

    const major = data.major_version;
    const majorVer = parseFloat(major, 10);
    const noVersion = major === 'N/A' || major === '' || major == null || Number.isNaN(majorVer);
    if (noVersion && msg) {
      return true;
    }
  }

  return false;
}

function isClientDomainFormatValid(rawInput) {
  const value = (rawInput || '').trim();
  if (!value) {
    return false;
  }
  const validRegex =
    /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\/.*)?$/;
  return validRegex.test(value);
}

async function postData(url, data) {
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: data,
    });

    const contentType = response.headers.get('content-type') || '';
    let payload = null;

    if (contentType.includes('application/json')) {
      payload = await response.json();
    } else {
      const responseText = await response.text();
      if (responseText.trim() !== '') {
        try {
          payload = JSON.parse(responseText);
        } catch (e) {
          payload = null;
        }
      }
    }

    if (payload && typeof payload === 'object') {
      return payload;
    }

    return {
      success: false,
      domain_check_failed: true,
    };
  } catch (error) {
    return {
      success: false,
      domain_check_failed: true,
      output: 'Network error while checking version.',
      debug: error && error.message ? error.message : 'Unknown error',
    };
  }
}

function applyVersionCheckResult(data, formRoot, checkVerBtn) {
  checkVerBtn.classList.remove('btn--checking-version');

  if (!data || typeof data !== 'object') {
    showCustomDomainError(formRoot);
    return;
  }

  // Prefer explicit backend error messages over the generic invalid-domain UI.
  if (data.success === false) {
    const apiMessage = backendErrorText(data);
    if (apiMessage) {
      showErrorMessage(formRoot, apiMessage);
      return;
    }
  }

  if (shouldUseCustomDomainError(data)) {
    showCustomDomainError(formRoot);
    return;
  }

  if (data.success === false) {
    formRoot.classList.add('form-error');
    return;
  }

  const formMsg = formRoot.querySelectorAll('.form-message > span');
  const majorVer = parseFloat(data.major_version, 10);
  const typo3 = isTypo3Site(data);
  let resultClass = 'form-error';

  if (typo3 && !Number.isNaN(majorVer) && majorVer >= LATEST_MAJOR) {
    resultClass = 'form-success';
  } else if (typo3 && !Number.isNaN(majorVer) && majorVer < LATEST_MAJOR) {
    resultClass = 'form-check-version';
  } else if (!typo3) {
    resultClass = 'form-error';
  }

  const applyResult = () => {
    if (resultClass === 'form-success' || resultClass === 'form-check-version') {
      formMsg.forEach((item) => {
        item.textContent = data.major_version;
      });
    }
    resetFormState(formRoot);
    formRoot.classList.add(resultClass);
  };

  applyResult();
  window.setTimeout(applyResult, 50);
}

function runVersionCheck(formRoot, checkUrl, checkVerBtn, checkVerForm) {
  const actionUrl = checkVerForm.getAttribute('action');
  const rawInput = checkUrl.value.trim();

  if (!isClientDomainFormatValid(rawInput)) {
    showCustomDomainError(formRoot);
    return;
  }

  const domain = rawInput.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  const formData = new FormData(checkVerForm);
  formData.set('tx_nsthemenitsan_versionchecker[domain]', domain);
  formData.set('tx_nsthemenitsan_versionchecker[action]', 'check');
  formData.set('tx_nsthemenitsan_versionchecker[controller]', 'VersionChecker');

  checkVerBtn.classList.add('btn--checking-version');
  resetFormState(formRoot);

  postData(actionUrl, formData)
    .then((data) => {
      applyVersionCheckResult(data, formRoot, checkVerBtn);
    })
    .catch(() => {
      checkVerBtn.classList.remove('btn--checking-version');
      showCustomDomainError(formRoot);
    });
}

function bindVersionCheckForms() {
  document.querySelectorAll('.check-typo3-teaser__form').forEach((formRoot) => {
    if (formRoot.classList.contains('sales-intelligence-form__form')) {
      return;
    }
    if (formRoot.getAttribute('data-ns-version-check-bound') === '1') {
      return;
    }

    const checkUrl = formRoot.querySelector('.check-url');
    const checkVerBtn = formRoot.querySelector('.btn--check-version');
    const checkVerForm = formRoot.querySelector('form');

    if (!checkUrl || !checkVerBtn || !checkVerForm) {
      return;
    }

    formRoot.setAttribute('data-ns-version-check-bound', '1');

    checkVerForm.addEventListener('submit', (event) => {
      event.preventDefault();
      runVersionCheck(formRoot, checkUrl, checkVerBtn, checkVerForm);
    });

    checkVerBtn.addEventListener('click', (event) => {
      event.preventDefault();
      runVersionCheck(formRoot, checkUrl, checkVerBtn, checkVerForm);
    });
  });
}

export const initVersionChecker = () => {
  bindVersionCheckForms();
};

initVersionChecker();

document.addEventListener('barba:after-enter', bindVersionCheckForms);
